import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Progress } from '@/components/ui/progress'
import {
  Users,
  Search,
  Filter,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  Award,
  ListChecks,
  CalendarClock,
  Eye,
  ChevronRight,
  MousePointerClick,
  Goal
} from 'lucide-react'
import { motion } from 'framer-motion'
import { TeamMember, TeamPerformanceSummary } from '@/services/api/managerService'
import type { ReviewSessionData } from '@/services/api/reviewService'
import { useJourneyViewMode } from '@/lib/journeyViewMode'
import { ViewModeToggle } from '@/components/journey/ViewModeToggle'
import { isReviewEligible, employmentCategoryLabel } from '@/lib/employment'

interface ManagerContext {
  managerId: string
  name: string
  role: string
  department: string
  campus: string
  directReports: number
  pendingActions: number
  upcomingReviews: number
}

interface TeamOverviewDashboardProps {
  teamMembers: TeamMember[]
  teamSummary?: TeamPerformanceSummary | null
  teamReviewSessions?: ReviewSessionData[]
  managerContext?: ManagerContext
  onScheduleReview: (memberId: string) => void
  onViewMember?: (memberId: string) => void | Promise<void>
  onConductReview?: (memberId: string, sessionId: string) => void
  onViewObjectives?: (memberId: string) => void
}

export default function TeamOverviewDashboard({
  teamMembers,
  onScheduleReview,
  onViewMember
}: TeamOverviewDashboardProps) {
  const [viewMode] = useJourneyViewMode()
  const isSimple = viewMode === 'simple'
  const [searchQuery, setSearchQuery] = useState('')
  // RAG-derived status options ('on-track' / 'needs-attention') were removed
  // along with the RAG display; only the lifecycle 'alerts' filter remains.
  const [statusFilter, setStatusFilter] = useState<'all' | 'alerts'>('all')
  const [performanceFilter, setPerformanceFilter] = useState<'all' | 'exceptional' | 'full' | 'not-yet-full' | 'weak'>('all')

  // Dedupe a member's alerts. The backend can surface the same alert twice
  // (e.g. "Ad-hoc Review overdue" from two overlapping signals), so we
  // collapse on a normalised (trimmed, case-insensitive) key while keeping
  // the first-seen original casing for display.
  const dedupeAlerts = (alerts: string[] | undefined | null): string[] => {
    if (!alerts || alerts.length === 0) return []
    const seen = new Set<string>()
    const result: string[] = []
    for (const alert of alerts) {
      const key = alert.trim().toLowerCase()
      if (key && !seen.has(key)) {
        seen.add(key)
        result.push(alert.trim())
      }
    }
    return result
  }

  const handleMemberClick = async (memberId: string) => {
    // Notify parent to open member details dialog
    if (onViewMember) {
      await onViewMember(memberId)
    }
  }

  // Split off ICA/Casual workers — they don't take part in the performance
  // cycle (no objectives/check-ins/calibration). They're shown SEPARATELY at
  // the bottom for context, and excluded from every review/progress statistic.
  const reviewableMembers = teamMembers.filter(isReviewEligible)
  const contractorMembers = teamMembers.filter(m => !isReviewEligible(m))

  // Calculate team statistics from actual member data (review-eligible only)
  const teamStats = {
    totalMembers: reviewableMembers.length,
    withAlerts: reviewableMembers.filter(m =>
      (dedupeAlerts(m.alerts).length > 0) || m.nextReview?.status === 'overdue'
    ).length,
    // Calm "team pulse" signal: how many people have captured / shared
    // anything in their portfolio this period. Used in Simple view in place
    // of the 4-tier classification distribution.
    sharedHeadlines: reviewableMembers.filter(m => (m.evidenceCount || 0) > 0).length,
    onTrack: reviewableMembers.filter(m => {
      // Count members where objectives are on track and no overdue reviews
      const objectives = m.objectives
      const hasOverdueReview = m.nextReview?.status === 'overdue'
      if (hasOverdueReview) return false
      if (!objectives) return m.status === 'on-track'
      // On track if no at-risk/off-track and has some active objectives
      const activeObjectives = (objectives.onTrack || 0) + (objectives.atRisk || 0) + (objectives.offTrack || 0)
      return objectives.atRisk === 0 && objectives.offTrack === 0 && activeObjectives > 0
    }).length,
    needsAttention: reviewableMembers.filter(m => {
      // Needs attention if: has alerts, overdue review, at-risk or off-track objectives
      const objectives = m.objectives
      const hasAlerts = m.alerts && m.alerts.length > 0
      const hasOverdueReview = m.nextReview?.status === 'overdue'
      const hasAtRisk = (objectives?.atRisk || 0) > 0
      const hasOffTrack = (objectives?.offTrack || 0) > 0
      return hasAlerts || hasOverdueReview || hasAtRisk || hasOffTrack
    }).length,
    upcomingReviews: reviewableMembers.filter(m =>
      m.nextReview?.status === 'scheduled' || m.nextReview?.status === 'pending-scheduling'
    ).length,
    overdueReviews: reviewableMembers.filter(m => m.nextReview?.status === 'overdue').length,
    performanceDistribution: {
      exceptional: reviewableMembers.filter(m => m.performance?.current === 'exceptional').length,
      full: reviewableMembers.filter(m => m.performance?.current === 'full').length,
      notYetFull: reviewableMembers.filter(m => m.performance?.current === 'not_yet_full').length,
      weak: reviewableMembers.filter(m => m.performance?.current === 'weak').length
    }
  }

  // Filter team members (review-eligible only; contractors render separately)
  const filteredMembers = reviewableMembers.filter(member => {
    const matchesSearch = (member.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         member.job_title?.toLowerCase().includes(searchQuery.toLowerCase())) ?? true
    
    const matchesStatus = statusFilter === 'all' ? true :
                         statusFilter === 'alerts' ? (member.alerts && member.alerts.length > 0) :
                         true

    const matchesPerformance = performanceFilter === 'all' ? true :
                              member.performance?.current === performanceFilter

    return matchesSearch && matchesStatus && matchesPerformance
  })

  const getPerformanceColor = (performance: string | null | undefined) => {
    // Normalize performance rating (handle both hyphen and underscore formats)
    const normalized = performance?.replace(/-/g, '_')
    switch (normalized) {
      case 'exceptional': return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'full': return 'bg-green-100 text-green-800 border-green-200'
      case 'not_yet_full': return 'bg-amber-100 text-amber-800 border-amber-200'
      case 'weak': return 'bg-red-100 text-red-800 border-red-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getTrendIcon = (trend: string | null | undefined) => {
    switch (trend) {
      case 'improving': return <TrendingUp className="h-4 w-4 text-green-600" />
      case 'maintaining': return <Minus className="h-4 w-4 text-blue-600" />
      case 'declining': return <TrendingDown className="h-4 w-4 text-red-600" />
      default: return <Minus className="h-4 w-4 text-gray-400" />
    }
  }

  const getReviewStatusColor = (status: string | undefined) => {
    // Simple view: calm, judgement-free stone palette so the team list never
    // shows a wall of red/amber status chips. Detailed view keeps RAG for HR.
    if (isSimple) {
      return status === 'completed'
        ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
        : 'bg-stone-100 text-stone-700 border border-stone-200'
    }
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800'
      case 'pending-scheduling': return 'bg-amber-100 text-amber-800'
      case 'overdue': return 'bg-red-100 text-red-800'
      case 'completed': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <>
      <div className="space-y-6">
        {/* Surface header — shared Simple ↔ Detailed toggle, top-right. */}
        <div className="flex items-center justify-end">
          <ViewModeToggle />
        </div>

        {/* Team Statistics Dashboard */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border-l-4 border-l-heritage-blue">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Team Members</p>
                  <p className="text-2xl md:text-3xl font-bold text-heritage-blue mt-1">{teamStats.totalMembers}</p>
                  <p className="text-xs text-gray-500 mt-1">Direct reports</p>
                </div>
                <Users className="h-8 w-8 md:h-12 md:w-12 text-heritage-blue/20" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="border-l-4 border-l-stone-400">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Your follow-ups</p>
                  <p className="text-2xl md:text-3xl font-bold text-stone-700 mt-1">{teamStats.needsAttention}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {teamStats.withAlerts > 0
                      ? `${teamStats.withAlerts} with something on your radar`
                      : 'Nothing pressing right now'}
                  </p>
                </div>
                <ListChecks className="h-8 w-8 md:h-12 md:w-12 text-stone-400/30" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Upcoming check-ins</p>
                  <p className="text-2xl md:text-3xl font-bold text-blue-600 mt-1">{teamStats.upcomingReviews}</p>
                  {teamStats.overdueReviews > 0 ? (
                    <p className="text-xs text-stone-500 mt-1">{teamStats.overdueReviews} waiting to be scheduled</p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">All scheduled</p>
                  )}
                </div>
                <CalendarClock className="h-8 w-8 md:h-12 md:w-12 text-blue-500/20" />
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Team pulse (Simple) — a calm, judgement-free summary that replaces the
          4-tier classification distribution. The full distribution stays
          available in Detailed view for HR / calibration. */}
      {isSimple ? (
        <div className="rounded-2xl border border-stone-200 bg-gradient-to-br from-stone-50 via-white to-stone-50 p-6 md:p-8">
          <p className="text-[11px] font-medium tracking-[0.22em] text-stone-500 uppercase mb-3">
            Your team this period
          </p>
          <p className="font-serif text-stone-900 text-[22px] md:text-[26px] leading-[1.3] tracking-tight">
            {teamStats.sharedHeadlines} of {teamStats.totalMembers}{' '}
            {teamStats.totalMembers === 1 ? 'person has' : 'people have'} shared their Headlines this period.
          </p>
          <p className="text-stone-600 text-sm md:text-base mt-2 max-w-xl">
            {teamStats.sharedHeadlines === teamStats.totalMembers && teamStats.totalMembers > 0
              ? 'Everyone has something captured — a good moment to listen and reflect together.'
              : 'A gentle nudge in your next 1:1 can help the rest start capturing their wins.'}
          </p>
        </div>
      ) : (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-heritage-blue" />
            Team Impact & Growth Distribution
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-purple-900">Exceptional</span>
                <span className="text-2xl font-bold text-purple-700">{teamStats.performanceDistribution.exceptional}</span>
              </div>
              <Progress 
                value={(teamStats.performanceDistribution.exceptional / teamStats.totalMembers) * 100} 
                className="h-2 bg-purple-200"
              />
              <p className="text-xs text-purple-600 mt-1">
                {Math.round((teamStats.performanceDistribution.exceptional / teamStats.totalMembers) * 100)}% of team
              </p>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-green-900">Full</span>
                <span className="text-2xl font-bold text-green-700">{teamStats.performanceDistribution.full}</span>
              </div>
              <Progress 
                value={(teamStats.performanceDistribution.full / teamStats.totalMembers) * 100} 
                className="h-2 bg-green-200"
              />
              <p className="text-xs text-green-600 mt-1">
                {Math.round((teamStats.performanceDistribution.full / teamStats.totalMembers) * 100)}% of team
              </p>
            </div>

            <div className="bg-amber-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-amber-900">Not Yet Full</span>
                <span className="text-2xl font-bold text-amber-700">{teamStats.performanceDistribution.notYetFull}</span>
              </div>
              <Progress 
                value={(teamStats.performanceDistribution.notYetFull / teamStats.totalMembers) * 100} 
                className="h-2 bg-amber-200"
              />
              <p className="text-xs text-amber-600 mt-1">
                {Math.round((teamStats.performanceDistribution.notYetFull / teamStats.totalMembers) * 100)}% of team
              </p>
            </div>

            <div className="bg-red-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-red-900">Weak</span>
                <span className="text-2xl font-bold text-red-700">{teamStats.performanceDistribution.weak}</span>
              </div>
              <Progress 
                value={(teamStats.performanceDistribution.weak / teamStats.totalMembers) * 100} 
                className="h-2 bg-red-200"
              />
              <p className="text-xs text-red-600 mt-1">
                {Math.round((teamStats.performanceDistribution.weak / teamStats.totalMembers) * 100)}% of team
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center gap-3 md:gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search team members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
              <SelectTrigger className="w-full sm:w-40 md:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="alerts">With Alerts</SelectItem>
              </SelectContent>
            </Select>

            {/* Classification filter is a calibration tool — hidden in the
                calm Simple view, available in Detailed for HR. */}
            {!isSimple && (
            <Select value={performanceFilter} onValueChange={(value: any) => setPerformanceFilter(value)}>
              <SelectTrigger className="w-full sm:w-40 md:w-48">
                <Award className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Impact & Growth Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="exceptional">Exceptional</SelectItem>
                <SelectItem value="full">Full</SelectItem>
                <SelectItem value="not-yet-full">Not Yet Full</SelectItem>
                <SelectItem value="weak">Weak</SelectItem>
              </SelectContent>
            </Select>
            )}
          </div>
        </CardContent>
      </Card>

        {/* Team Members List */}
        <div className="space-y-4">
          {filteredMembers.map((member, index) => (
          <motion.div
            key={member.employee_id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Card
              className={`hover:shadow-lg transition-all cursor-pointer hover:border-heritage-blue group relative ${dedupeAlerts(member.alerts).length > 0 ? 'border-l-4 border-l-stone-300' : ''}`}
              onClick={() => handleMemberClick(member.employee_id)}
              data-demo="team-member-card"
              data-demo-member-id={member.employee_id}
            >
              <CardContent className="p-4 md:p-6">
                {/* Click to View Indicator */}
                <div className="absolute top-3 right-3 md:top-4 md:right-4 flex items-center gap-1.5 text-xs text-gray-400 group-hover:text-heritage-blue transition-colors">
                  <MousePointerClick className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  <span className="font-medium hidden sm:inline">Select to view details</span>
                  <ChevronRight className="h-3.5 w-3.5 md:h-4 md:w-4" />
                </div>

                <div className="flex items-start">
                  {/* Member Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-heritage-blue/10 flex items-center justify-center">
                        <span className="text-base md:text-lg font-bold text-heritage-blue">
                          {member.display_name?.split(' ').map(n => n[0]).join('') || '??'}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-lg font-semibold text-gray-900">{member.display_name || 'Unknown'}</h3>
                          {member.acting_people_leader && (
                            <Badge
                              className="text-xs bg-amber-50 text-amber-800 border border-amber-300"
                              title={`Line manager seat is vacant${member.vacant_line_seat ? ` (${member.vacant_line_seat})` : ''} — you are the stand-in people leader`}
                            >
                              Stand-in
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{member.job_title || 'No Title'} • {member.department || 'N/A'}</p>
                        {member.acting_people_leader && (
                          <p className="text-xs text-amber-700 mt-0.5">
                            Line manager seat vacant{member.vacant_line_seat ? `: ${member.vacant_line_seat}` : ''} — you act as people leader
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
                      {/* Objectives — lists what the member is actually working
                          on (titles) rather than RAG status, which was removed.
                          Top few non-rejected titles + a "+N more" overflow. */}
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1">
                          <Goal className="h-3 w-3" />
                          Objectives ({member.objectives?.total || 0})
                        </p>
                        <div className="space-y-1.5">
                          {(() => {
                            // team-summary returns active_objectives; fall back to it now
                            // that the per-member fan-out (which set planObjectives) is gone.
                            const titled = (member.planObjectives || (member as any).active_objectives || [])
                              .filter((o: any) => o?.title && (o.status || '').toLowerCase() !== 'rejected')
                            if (titled.length === 0) {
                              return (
                                <p className="text-xs text-gray-400">
                                  {(member.objectives?.total || 0) > 0
                                    ? 'Objectives set for this period'
                                    : 'No objectives yet'}
                                </p>
                              )
                            }
                            const shown = titled.slice(0, 3)
                            const remaining = titled.length - shown.length
                            return (
                              <>
                                <ul className="space-y-1">
                                  {shown.map((o: any, i: number) => (
                                    <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
                                      <span className={`mt-1 h-1 w-1 rounded-full flex-shrink-0 ${isSimple ? 'bg-stone-400' : 'bg-heritage-blue/60'}`} />
                                      <span className="line-clamp-1">{o.title}</span>
                                    </li>
                                  ))}
                                </ul>
                                {remaining > 0 && (
                                  <p className="text-xs text-gray-400">+{remaining} more</p>
                                )}
                              </>
                            )
                          })()}
                          {(member.objectives?.pending_approval || 0) > 0 && (
                            <div className="flex items-center justify-between text-xs pt-0.5">
                              <span className={isSimple ? 'text-stone-500' : 'text-blue-700'}>Pending Approval</span>
                              <span className={`font-semibold ${isSimple ? 'text-stone-600' : 'text-blue-700'}`}>{member.objectives?.pending_approval || 0}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Performance Status — the classification chip is a
                          calibration signal, hidden in the calm Simple view.
                          Detailed keeps it for HR / calibration. */}
                      {!isSimple && (
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1">
                          <Award className="h-3 w-3" />
                          Impact & Growth Indicator
                        </p>
                        <div className="space-y-2">
                          {member.performance?.current ? (
                            <>
                              <Badge className={`${getPerformanceColor(member.performance.current)} border text-xs capitalize`}>
                                {member.performance.current.replace(/-/g, ' ').replace(/_/g, ' ')}
                              </Badge>
                              <div className="flex items-center gap-2 text-xs text-gray-600">
                                {getTrendIcon(member.performance.trend || 'maintaining')}
                                <span className="capitalize">{member.performance.trend || 'maintaining'}</span>
                              </div>
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">No indicator yet</span>
                          )}
                        </div>
                      </div>
                      )}

                      {/* Next Review */}
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Next Check-in
                        </p>
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-gray-900">{member.nextReview?.type || 'Not Scheduled'}</p>
                          {member.nextReview?.date && (
                            <p className="text-xs text-gray-600">{new Date(member.nextReview.date).toLocaleDateString()}</p>
                          )}
                          <Badge className={`${getReviewStatusColor(member.nextReview?.status || 'pending-scheduling')} text-xs`}>
                            {(member.nextReview?.status || 'pending-scheduling').replace('-', ' ')}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {/* On your radar — calmer framing of the old red "Alerts"
                        panel. Neutral stone palette + a soft eye icon instead
                        of a warning triangle, and deduped so the same item
                        (e.g. "Ad-hoc Review overdue") never repeats. */}
                    {dedupeAlerts(member.alerts).length > 0 && (
                      <div className="mt-4 bg-stone-50 border border-stone-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <Eye className="h-4 w-4 text-stone-500 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-stone-700 mb-1">On your radar</p>
                            <ul className="space-y-1">
                              {dedupeAlerts(member.alerts).map((alert, i) => (
                                <li key={i} className="text-xs text-stone-600">• {alert}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          ))}
        </div>
        
        {filteredMembers.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No team members match your filters</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => {
                  setSearchQuery('')
                  setStatusFilter('all')
                  setPerformanceFilter('all')
                }}
              >
                Clear Filters
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ICA / Casual / Learnership — shown for context only. They don't take
            part in objectives, scheduled check-ins or calibration, so they're
            kept out of the statistics and the main list above. */}
        {contractorMembers.length > 0 && (
          <div className="space-y-3 pt-4 border-t border-stone-200">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-700">
                Not in performance reviews
              </h3>
              <Badge className="text-xs bg-stone-100 text-stone-600 border border-stone-200">
                {contractorMembers.length}
              </Badge>
            </div>
            <p className="text-xs text-gray-500 -mt-1">
              ICA, Casual &amp; Learnership workers are shown here for context. They don't have
              objectives, scheduled check-ins or calibration ratings.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {contractorMembers.map((member) => (
                <Card
                  key={member.employee_id}
                  className="border-stone-200 bg-stone-50/50 hover:shadow-sm transition-all cursor-pointer"
                  onClick={() => handleMemberClick(member.employee_id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-stone-200/70 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-stone-600">
                          {member.display_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '??'}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <h4 className="text-sm font-semibold text-gray-900 truncate">{member.display_name || 'Unknown'}</h4>
                          {member.acting_people_leader && (
                            <Badge className="text-[10px] bg-amber-50 text-amber-800 border border-amber-300 flex-shrink-0" title="Line manager seat vacant — you are the stand-in people leader">
                              Stand-in
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 truncate">{member.job_title || 'No Title'}</p>
                      </div>
                      <Badge className="text-xs bg-stone-200 text-stone-700 border border-stone-300 flex-shrink-0">
                        {employmentCategoryLabel(member)}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

    </>
  )
}
